import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Snowflake } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Conta criada!");
      nav(u.role === "admin" ? "/admin" : "/painel", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 bg-grid px-4 py-10">
      <Card className="w-full max-w-md bg-slate-900/80 backdrop-blur-xl border-sky-500/20 glow-cyan">
        <CardContent className="p-8">
          <Link to="/" className="flex items-center gap-2 justify-center mb-8">
            <Logo size={56} className="rounded-full" />
            <div className="flex flex-col leading-none">
              <span className="text-lg font-black text-white">TSBRASIL</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-sky-400">Ar-Condicionado</span>
            </div>
          </Link>

          <h1 className="text-2xl font-bold text-white text-center mb-2">Criar conta</h1>
          <p className="text-sm text-slate-400 text-center mb-6">Comece a agendar atendimentos em segundos</p>

          <Button
            onClick={googleLogin}
            variant="outline"
            className="w-full bg-white hover:bg-slate-100 text-slate-900 border-slate-200 font-semibold h-11 mb-4"
            data-testid="google-register-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Cadastrar com Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-slate-700 flex-1"></div>
            <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">ou</span>
            <div className="h-px bg-slate-700 flex-1"></div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome completo</Label>
              <Input required value={form.name} onChange={onChange("name")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-slate-300">E-mail</Label>
              <Input required type="email" value={form.email} onChange={onChange("email")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-slate-300">Telefone / WhatsApp</Label>
              <Input value={form.phone} onChange={onChange("phone")} placeholder="(11) 99999-9999" className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="register-phone-input" />
            </div>
            <div>
              <Label className="text-slate-300">Senha</Label>
              <Input required type="password" value={form.password} onChange={onChange("password")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold h-11 btn-shine" data-testid="register-submit-btn">
              {loading ? "Criando..." : "Criar conta"}
            </Button>
          </form>

          <p className="text-sm text-slate-400 text-center mt-6">
            Já tem conta?{" "}
            <Link to="/entrar" className="text-sky-400 hover:text-sky-300 font-medium" data-testid="goto-login">Entrar</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
